import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { uploadToImgBB } from '../../utils/imgbb';
import { showToast } from '../../components/ui/Toast';

const DriverOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('activos');
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deliveryPhoto, setDeliveryPhoto] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/orders/driver/orders');
      setOrders(data.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/orders/${id}/status`, { status });
      fetchOrders();
      showToast(`✅ Estado actualizado a ${status}`, 'success');
    } catch (error) {
      showToast('Error al actualizar', 'error');
    }
  };

  const updateLocation = (order) => {
    if (!navigator.geolocation) {
      showToast('GPS no disponible', 'error');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await api.put(`/orders/${order.id}/driver-location`, {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          showToast('📍 Ubicación actualizada', 'success');
        } catch (error) {
          showToast('Error al actualizar ubicación', 'error');
        }
      },
      () => showToast('No se pudo obtener ubicación', 'error'),
      { enableHighAccuracy: true }
    );
  };

  const openMaps = (address, lat, lng) => {
    let url;
    if (lat && lng) {
      url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    } else {
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }
    window.open(url, '_blank');
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDeliveryPhoto(file);
      setPreviewPhoto(URL.createObjectURL(file));
    }
  };

  const handleConfirmDelivery = async () => {
    if (!deliveryPhoto) {
      showToast('Debes subir una foto de la entrega', 'error');
      return;
    }

    setUploading(true);
    try {
      // Subir a imgBB
      const upload = await uploadToImgBB(deliveryPhoto);
      
      if (!upload.success) {
        showToast('Error al subir foto: ' + upload.error, 'error');
        setUploading(false);
        return;
      }

      // Marcar como entregado con la foto
      await api.post(`/orders/${selectedOrder.id}/delivery-photo`, {
        photoUrl: upload.url
      });

      showToast('✅ Entrega confirmada con foto', 'success');
      setShowDeliveryModal(false);
      setSelectedOrder(null);
      setDeliveryPhoto(null);
      setPreviewPhoto(null);
      fetchOrders();
    } catch (error) {
      showToast('Error: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setUploading(false);
    }
  };

  const statusConfig = {
    pendiente: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
    aceptado: { label: 'Aceptado', color: 'bg-blue-100 text-blue-700' },
    preparando: { label: 'Preparando', color: 'bg-purple-100 text-purple-700' },
    en_camino: { label: 'En camino', color: 'bg-orange-100 text-orange-700' },
    entregado: { label: 'Entregado', color: 'bg-green-100 text-green-700' },
    cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-700' }
  };

  const filtered = filter === 'activos'
    ? orders.filter(o => o.status === 'aceptado' || o.status === 'en_camino')
    : filter === 'entregados'
    ? orders.filter(o => o.status === 'entregado')
    : orders;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl md:text-3xl font-serif font-bold">📋 Mis Entregas</h1>
        <button
          onClick={fetchOrders}
          className="bg-gold text-white px-4 py-2 rounded-full text-sm"
        >
          🔄
        </button>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { value: 'activos', label: '🔥 Activos', count: orders.filter(o => o.status === 'aceptado' || o.status === 'en_camino').length },
          { value: 'entregados', label: '✅ Entregados', count: orders.filter(o => o.status === 'entregado').length },
          { value: 'todos', label: '📋 Todos', count: orders.length }
        ].map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
              filter === f.value ? 'bg-primary text-white' : 'bg-white text-gray-600 border'
            }`}
          >
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center text-gray-500">
          <div className="text-6xl mb-3">🎉</div>
          <p>No hay entregas</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map(order => (
            <div key={order.id} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
              <div className="p-4 bg-gradient-to-r from-background to-background-alt border-b">
                <div className="flex justify-between items-center">
                  <div className="font-bold">#{order.order_number}</div>
                  <span className={`text-xs px-3 py-1 rounded-full font-semibold ${statusConfig[order.status]?.color}`}>
                    {statusConfig[order.status]?.label}
                  </span>
                </div>
              </div>

              <div className="p-4 border-b">
                <div className="text-xs text-gray-500 uppercase mb-2">Cliente</div>
                <div className="font-semibold">{order.customer_info?.name}</div>
                <div className="text-sm text-gray-600">📱 {order.customer_info?.phone}</div>
                <div className="text-sm text-gray-600 mt-1">📍 {order.customer_info?.address}</div>
                
                {order.delivery_location?.lat && (
                  <div className="text-xs text-green-600 mt-1">
                    ✅ Ubicación GPS capturada
                  </div>
                )}

                {order.customer_info?.notes && (
                  <div className="mt-2 text-xs italic text-gray-500 bg-yellow-50 p-2 rounded">
                    💬 {order.customer_info.notes}
                  </div>
                )}
              </div>

              <div className="p-4 border-b">
                <div className="text-xs text-gray-500 uppercase mb-2">Productos</div>
                {order.items?.map((item, i) => (
                  <div key={i} className="text-sm py-1">
                    {item.quantity}x {item.name}
                  </div>
                ))}
              </div>

              <div className="p-4 bg-gray-50 border-b">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total</span>
                  <div className="text-right">
                    <div className="font-bold text-gold">${Number(order.total_usd).toFixed(2)}</div>
                    <div className="text-xs text-gray-600">Bs {Number(order.total_bs).toFixed(2)}</div>
                  </div>
                </div>
              </div>

              <div className="p-4 space-y-2">
                {order.status === 'aceptado' && (
                  <button
                    onClick={() => updateStatus(order.id, 'en_camino')}
                    className="w-full py-3 bg-orange-500 text-white rounded-full font-semibold"
                  >
                    🚗 Iniciar entrega
                  </button>
                )}

                {order.status === 'en_camino' && (
                  <>
                    <button
                      onClick={() => openMaps(order.customer_info?.address, order.delivery_location?.lat, order.delivery_location?.lng)}
                      className="w-full py-3 bg-blue-500 text-white rounded-full font-semibold"
                    >
                      🗺️ Ver ruta
                    </button>
                    <button
                      onClick={() => updateLocation(order)}
                      className="w-full py-3 bg-gray-100 text-gray-700 rounded-full font-semibold"
                    >
                      📍 Actualizar ubicación
                    </button>
                    <button
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowDeliveryModal(true);
                      }}
                      className="w-full py-3 bg-green-500 text-white rounded-full font-semibold"
                    >
                      📸 Confirmar entrega
                    </button>
                  </>
                )}

                {order.status === 'entregado' && (
                  <div className="text-center text-green-600 font-medium py-2">
                    ✅ Entrega completada
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal foto entrega */}
      {showDeliveryModal && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDeliveryModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-5 border-b flex justify-between items-center">
              <h2 className="text-lg font-bold">📸 Confirmar Entrega</h2>
              <button onClick={() => setShowDeliveryModal(false)} className="text-2xl">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-yellow-50 p-3 rounded-lg text-sm text-yellow-800">
                📸 Toma una foto de la entrega como comprobante
              </div>

              <div className="border-2 border-dashed rounded-xl p-4 text-center">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoChange}
                  className="hidden"
                  id="delivery-photo"
                />
                <label htmlFor="delivery-photo" className="cursor-pointer block">
                  {previewPhoto ? (
                    <img src={previewPhoto} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
                  ) : (
                    <div>
                      <div className="text-5xl mb-2">📷</div>
                      <p className="text-sm font-medium">Toca para tomar/subir foto</p>
                    </div>
                  )}
                </label>
              </div>

              <button
                onClick={handleConfirmDelivery}
                disabled={uploading || !deliveryPhoto}
                className="w-full py-3 bg-green-500 text-white rounded-full font-semibold disabled:opacity-50"
              >
                {uploading ? '📤 Subiendo...' : '✅ Confirmar entrega'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverOrders;
